/* eslint-disable no-restricted-globals */
/* eslint-disable react/no-array-index-key */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Button } from '@saleshandy/design-system';
import {
  Users,
  World,
  Check,
  Copy,
  DotsVertical,
  LinkedinSquareFilled,
  ArrowsDiagonal,
  AddTag,
  RemoveTag,
  AddList,
  RemoveList,
} from '@saleshandy/icons';
import { Dropdown, Spinner } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import useClosePopoverOnScroll from './hooks/use-close-popover-on-scroll';
import CompanySocialsCell from './cell-renderers/company-socials-cell';
import CellFeedbackButtons from './cell-renderers/cell-feedback-buttons';
import {
  LeadRevealFeedbackSentiment,
  submitCompanyFeedbackAPI,
} from '../../helpers/leads.api';
import {
  OverlayPopover,
  OverlayTooltip,
  Placement,
} from '../../../../shared/design-system/components/overlay';
import ProspectTagsRow from '../../../../shared/design-system/components/molecules/prospect-tags-row';
import { V2Company, LeadFinderSecondaryTab, ExecutiveData } from '../../types';
import { formatNumberWithCommas } from '../../../../shared/utils/format-number-with-commas';
import TruncatedTextWithAutoTooltip from '../../../../shared/components/truncated-text-with-auto-tooltip';
import { accessibleOnClick, toTelHref } from '../../../../shared/utils';
import { ALL_COMPANY_COLUMN_FIELDS } from './company-column-groups';
import CompanyDataCard, {
  CompanyDataCardColumn,
  CompanyDataCardRow,
} from '../company-data-card/company-data-card';
import { formatNumberToKM } from '../../../sequence/helpers/helper';

const columnHelper = createColumnHelper<V2Company>();

// Helper function to check if enrichment teaser should be shown
// When isEnriched is true: show actual data. Otherwise: show teaser ("Show Full Enrichment" or "Enriching..." based on isRevealing)
const shouldShowEnrichmentTeaser = (
  activeSubTab: LeadFinderSecondaryTab,
  isEnriched: boolean | undefined,
  columnId: string,
  isFreePlanRestricted: boolean,
): boolean => {
  // Company Description should remain visible without enrichment
  // for non-free users only.
  if (columnId === 'company_description' && !isFreePlanRestricted) {
    return false;
  }

  // If company is enriched, show actual data
  if (isEnriched === true) {
    return false;
  }

  // Check if column is non-default (isDefault is false or undefined)
  const columnField = ALL_COMPANY_COLUMN_FIELDS.find(
    (field) => field.id === columnId,
  );
  if (!columnField) {
    return false;
  }
  const isDefaultColumn = columnField?.isDefault === true;

  // Show teaser only for non-default columns when not enriched
  const isEnrichedBool = Boolean(isEnriched);
  return !isDefaultColumn && !isEnrichedBool;
};

// Component to render enrichment teaser text
interface EnrichmentTeaserCellProps {
  companyId: number;
  onRevealCompany: (companyId: number) => void;
  isEnriching?: boolean;
}

const EnrichmentTeaserCell: React.FC<EnrichmentTeaserCellProps> = ({
  companyId,
  onRevealCompany,
  isEnriching = false,
}) => {
  const handleClick = () => {
    if (!isEnriching) {
      onRevealCompany(companyId);
    }
  };

  return (
    <div
      className="v2-lead-company-text-cell"
      {...accessibleOnClick(handleClick)}
      role="button"
      tabIndex={0}
    >
      <OverlayTooltip
        className="v2-lead-company-text-cell-tooltip"
        placement={Placement.Bottom}
        text={
          <div className="d-flex flex-column">
            <span>Enrich & save this company</span>
            <span style={{ fontSize: '12px', fontWeight: '400' }}>
              Uses 0.1 credits
            </span>
          </div>
        }
      >
        <span className={isEnriching ? 'enriching-text' : ''}>
          {isEnriching ? (
            <div className="map-field-verify">
              <Spinner
                animation="border"
                className="map-field-verify__spinner"
              />
              <span className="enriching-text">Enriching...</span>
            </div>
          ) : (
            'Enrich Full Profile'
          )}
        </span>
      </OverlayTooltip>
    </div>
  );
};

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export const formatCurrencyValue = (value?: number | null) => {
  if (value === null || value === undefined) {
    return undefined;
  }
  return currencyFormatter.format(value);
};

const formatEmployeeCount = (count?: number | null) => {
  if (count === null || count === undefined) {
    return undefined;
  }
  return formatNumberWithCommas(count);
};

/** Parse string to number (strip $, €, commas, spaces). Returns NaN if not a single valid number. */
const parseFormattedNumber = (value: string): number => {
  const stripped = value.replace(/[$€,\s]/g, '');
  const num = parseFloat(stripped);
  return Number.isNaN(num) ? Number.NaN : num;
};

/** Format a string value (e.g. "$1,234,567" or "1,234") to K/M/B. Returns original if unparseable. */
const formatValueToKM = (value: string, isCurrency: boolean): string => {
  const num = parseFormattedNumber(value);
  if (Number.isNaN(num)) {
    return value;
  }
  const formatted = formatNumberToKM(num);
  return isCurrency ? `$${formatted}` : formatted;
};

/** Format a numeric amount as currency in K/M/B (e.g. for Funding Round Amount chips). */
export const formatCurrencyToKM = (
  value?: number | null,
): string | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }
  return `$${formatNumberToKM(value)}`;
};

// Text Cell component - truncates with tooltip like lead table
const TextCell: React.FC<{
  value?: string | null;
  className?: string;
  allowCopy?: boolean;
  tooltipPlacement?: Placement;
  formatToKM?: boolean;
  isCurrency?: boolean;
}> = ({
  value,
  className = '',
  allowCopy = false,
  tooltipPlacement = Placement.Bottom,
  formatToKM = false,
  isCurrency = false,
}) => {
  const [copiedValue, setCopiedValue] = useState<string | null>(null);
  const displayValue =
    formatToKM && value != null && value.trim() !== ''
      ? formatValueToKM(value, isCurrency)
      : value;
  const handleCopy = () => {
    if (allowCopy && displayValue) {
      navigator.clipboard.writeText(displayValue);
      setCopiedValue(displayValue);
      setTimeout(() => setCopiedValue(null), 2000);
    }
  };
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '')
  ) {
    return <span className={`v2-company-text-cell ${className}`}>-</span>;
  }
  return (
    <span className={`v2-company-text-cell ${className}`}>
      <TruncatedTextWithAutoTooltip
        text={displayValue ?? ''}
        placement={tooltipPlacement}
      />
      {allowCopy && (
        <div
          className="v2-company-text-cell-copy-icon"
          {...accessibleOnClick(handleCopy)}
        >
          <OverlayTooltip
            placement={Placement.Bottom}
            text={copiedValue === displayValue ? 'Copied' : 'Copy'}
          >
            <span>{copiedValue === displayValue ? <Check /> : <Copy />}</span>
          </OverlayTooltip>
        </div>
      )}
    </span>
  );
};

// Company Name Cell with logo - clickable to website, name truncates with tooltip
const CompanyNameCell: React.FC<{
  company: V2Company;
  onProfileClick?: (company: V2Company, rect: DOMRect) => void;
  feedbackProps?: {
    companyId: number;
    initialSentiment?: 'positive' | 'negative';
    initialMessage?: string;
    onFeedbackSubmitted?: (
      sentiment: LeadRevealFeedbackSentiment,
      message?: string,
    ) => void;
  };
}> = ({ company, onProfileClick, feedbackProps }) => {
  const [isFeedbackPopoverOpen, setIsFeedbackPopoverOpen] = useState(false);
  const companyName = company.company_name || company.name || '-';
  const logoUrl = company.company_logo_url || company.logo_url;
  const websiteUrl = company.website;

  const handleProfileClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (onProfileClick) {
      const button = e.currentTarget.querySelector(
        'button',
      ) as HTMLElement | null;
      const anchorElement = button || e.currentTarget;
      const buttonRect = anchorElement.getBoundingClientRect();
      const indicator = e.currentTarget.querySelector(
        '.v2-company-modal-open-indicator',
      ) as HTMLElement | null;
      const indicatorRect = indicator?.getBoundingClientRect();
      const anchorRight =
        indicatorRect && indicatorRect.width > 0
          ? indicatorRect.right
          : buttonRect.right;
      const rect = new DOMRect(
        buttonRect.left,
        buttonRect.top,
        anchorRight - buttonRect.left,
        buttonRect.height,
      );
      onProfileClick(company, rect);
    }
  };

  const logoContent = logoUrl && (
    <span
      {...(onProfileClick
        ? accessibleOnClick((e) => {
            e.stopPropagation();
            onProfileClick(
              company,
              (e.currentTarget as HTMLElement).getBoundingClientRect(),
            );
          })
        : {})}
    >
      <img
        src={logoUrl}
        alt={companyName}
        className="v2-company-logo"
        loading="lazy"
      />
    </span>
  );

  const nameText = onProfileClick ? (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div className="v2-company-name" onClick={handleProfileClick}>
      <div className="v2-company-name--clickable">
        <TruncatedTextWithAutoTooltip
          text={companyName}
          placement={Placement.Bottom}
        />
        <span className="v2-company-modal-open-indicator" aria-hidden="true">
          <ArrowsDiagonal width={14} height={14} />
        </span>
      </div>
    </div>
  ) : (
    <span className="v2-company-name">
      <TruncatedTextWithAutoTooltip
        text={companyName}
        placement={Placement.Bottom}
      />
    </span>
  );

  const feedbackNode = feedbackProps && (
    <div
      className={`cell-feedback-btns-wrapper${
        isFeedbackPopoverOpen ? ' cell-feedback-btns-wrapper--active' : ''
      }`}
    >
      <CellFeedbackButtons
        leadId={feedbackProps.companyId}
        initialSentiment={feedbackProps.initialSentiment}
        initialMessage={feedbackProps.initialMessage}
        onFeedbackSubmitted={feedbackProps.onFeedbackSubmitted}
        onPopoverOpenChange={setIsFeedbackPopoverOpen}
        submitFn={(entityId, sentiment, message) =>
          submitCompanyFeedbackAPI({
            companyId: entityId,
            sentiment,
            ...(message && { message }),
          })
        }
      />
    </div>
  );

  if (websiteUrl) {
    return (
      <div className="v2-company-name-cell">
        {/* <a
          href={websiteUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="v2-company-name-link-icon"
          onClick={(e) => e.stopPropagation()}
          title={websiteUrl}
        >
          <World width={14} height={14} />
        </a> */}
        {logoContent}
        {nameText}
        {feedbackNode}
      </div>
    );
  }

  return (
    <div className="v2-company-name-cell">
      {logoContent}
      {nameText}
      {feedbackNode}
    </div>
  );
};

// Domain Cell with link - domain truncates with tooltip
const DomainCell: React.FC<{
  domain?: string | null;
  website?: string | null;
}> = ({ domain, website }) => {
  const domainValue = domain || '';
  if (!domainValue) {
    return <span className="v2-company-text-cell">-</span>;
  }

  const url = website || `https://${domainValue}`;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="v2-company-domain-link"
      onClick={(e) => e.stopPropagation()}
    >
      <World width={14} height={14} />
      <TruncatedTextWithAutoTooltip
        text={domainValue}
        placement={Placement.Bottom}
      />
    </a>
  );
};

const RowPlacementContext = createContext<Placement>(Placement.Bottom);

const CompanySocialsCellWithContext: React.FC<{
  socialUrls?: string[] | null;
}> = ({ socialUrls }) => {
  const placement = useContext(RowPlacementContext);
  return <CompanySocialsCell socialUrls={socialUrls} placement={placement} />;
};

const getTagsRowIsPosition = (rowIndex: number, totalRows: number): boolean =>
  totalRows <= 10 || rowIndex < totalRows - 3;

// Array/Record formatter - formats Record<string, number> as "Key: Value, Key2: Value2"
// const formatRecord = (
//   record?: Record<string, number | null> | null,
// ): string => {
//   if (!record) return '';
//   const entries = Object.entries(record)
//     .filter(([, value]) => value !== null && value !== undefined && value > 0)
//     .map(([key, value]) => `${key}: ${value}`)
//     .slice(0, 5); // Limit to 5 entries
//   return entries.join(', ');
// };

// // Array formatter - formats array as comma-separated string
// const formatArray = (arr?: (string | number)[] | null, limit = 5): string => {
//   if (!arr || arr.length === 0) return '';
//   return (
//     arr.slice(0, limit).join(', ') +
//     (arr.length > limit ? ` (+${arr.length - limit})` : '')
//   );
// };

// Format label key - converts "founder" to "Founder", "other_management" to "Other Management", "clevel" to "C-Level"
const formatLabelKey = (key: string): string => {
  // Handle special cases
  const specialCases: Record<string, string> = {
    clevel: 'C-Level',
    vp: 'VP',
    hr: 'HR',
  };

  if (specialCases[key.toLowerCase()]) {
    return specialCases[key.toLowerCase()];
  }

  // Replace underscores with spaces and capitalize each word
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const CountPillWithPopover: React.FC<{
  count: number;
  content: React.ReactNode;
  className?: string;
  placement?: Placement;
  flip?: boolean;
}> = ({ count, content, className = '', placement: placementProp, flip }) => {
  const [showPopover, setShowPopover] = useState(false);
  const contextPlacement = useContext(RowPlacementContext);
  const placement =
    placementProp !== undefined ? placementProp : contextPlacement;

  useClosePopoverOnScroll(showPopover, () => setShowPopover(false));

  if (count === null || count === undefined) {
    return <span className={`v2-company-count-pill ${className}`}>-</span>;
  }

  if (count <= 0) {
    return null;
  }

  return (
    <OverlayPopover
      show={showPopover}
      onToggle={setShowPopover}
      placement={placement}
      flip={flip}
      className="prospect-tag-popover v2-company-popover"
      content={content}
      rootClose
    >
      <span
        className={`v2-company-count-pill ${
          showPopover ? 'active' : ''
        } ${className}`}
        {...accessibleOnClick((e) => {
          e.stopPropagation();
          setShowPopover(!showPopover);
        })}
      >
        +{count}
      </span>
    </OverlayPopover>
  );
};

// Array Cell with Chip - shows first item as text and "+N" as chip
export const ArrayCellWithChip: React.FC<{
  items?: (string | number)[] | null;
  className?: string;
  formatter?: (item: string | number) => string;
  /** When true, popover rows show label (left) and value (right) with space-between, splitting each item by ": " */
  popoverLabelValue?: boolean;
  /** Number of items to show before the +N pill. Defaults to 1 (original horizontal layout). Values > 1 render a vertical column stack. */
  maxVisible?: number;
  placement?: Placement;
  flip?: boolean;
}> = ({
  items,
  className = '',
  formatter,
  popoverLabelValue = false,
  maxVisible = 1,
  placement,
  flip,
}) => {
  if (!items || items.length === 0) {
    return <span className={`v2-company-text-cell ${className}`}>-</span>;
  }

  const formatItem = (item: string | number) =>
    formatter ? formatter(item) : String(item);

  const visibleItems = items.slice(0, maxVisible);
  const remainingItems = items.slice(maxVisible);

  const popoverContent = (
    <div className="overlay-popover-content-wrap">
      {remainingItems.map((item, index) => {
        const formatted = formatItem(item);
        if (popoverLabelValue) {
          const colonIdx = formatted.indexOf(': ');
          const label =
            colonIdx >= 0 ? formatted.slice(0, colonIdx) : formatted;
          const value = colonIdx >= 0 ? formatted.slice(colonIdx + 2) : '';
          return (
            <div
              key={index}
              className="text-wrap"
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <span className="regular-2">{label}</span>
              <span className="regular-2">{value}</span>
            </div>
          );
        }
        return (
          <div key={index} className="text-wrap">
            <span className="regular-2">{formatted}</span>
          </div>
        );
      })}
    </div>
  );

  // maxVisible > 1: vertical stack (used in modal context, e.g. Past Companies)
  if (maxVisible > 1) {
    return (
      <div
        className={`v2-company-array-cell v2-company-array-cell--multi ${className}`}
      >
        {visibleItems.map((item, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="v2-company-cell-text-wrap">
            <TruncatedTextWithAutoTooltip
              text={formatItem(item)}
              placement={Placement.Bottom}
            />
          </div>
        ))}
        {remainingItems.length > 0 && (
          <CountPillWithPopover
            count={remainingItems.length}
            content={popoverContent}
            placement={placement}
            flip={flip}
          />
        )}
      </div>
    );
  }

  // maxVisible === 1 (default): original horizontal layout for table cells
  if (items.length === 1) {
    return (
      <span className={`v2-company-text-cell ${className}`}>
        <TruncatedTextWithAutoTooltip
          text={formatItem(items[0])}
          placement={Placement.Bottom}
        />
      </span>
    );
  }

  return (
    <div
      className={`v2-company-text-cell v2-company-array-cell v2-company-text-and-chip-cell ${className}`}
    >
      <span className="v2-company-cell-text-wrap v2-company-text-cell">
        <TruncatedTextWithAutoTooltip
          text={formatItem(visibleItems[0])}
          placement={Placement.Bottom}
        />
      </span>
      <CountPillWithPopover
        count={remainingItems.length}
        content={popoverContent}
        placement={placement}
        flip={flip}
      />
    </div>
  );
};

// Record Cell with Chip - shows first entry as text and "+N" as chip
const RecordCellWithChip: React.FC<{
  record?: Record<string, number | null> | null;
  className?: string;
  formatter?: (key: string, value: number) => string;
}> = ({ record, className = '', formatter }) => {
  if (!record) {
    return <span className={`v2-company-text-cell ${className}`}>-</span>;
  }

  const entries = Object.entries(record).filter(
    ([, value]) => value !== null && value !== undefined && value > 0,
  );

  if (entries.length === 0) {
    return <span className={`v2-company-text-cell ${className}`}>-</span>;
  }

  const formatEntry = (key: string, value: number) => {
    if (formatter) {
      return formatter(key, value);
    }
    return `${key}: ${value}`;
  };

  // If only one entry, just show it as text (truncated)
  if (entries.length === 1) {
    const [key, value] = entries[0];
    return (
      <span className={`v2-company-text-cell ${className}`}>
        <TruncatedTextWithAutoTooltip
          text={formatEntry(key, value)}
          placement={Placement.Bottom}
        />
      </span>
    );
  }

  const firstEntry = formatEntry(entries[0][0], entries[0][1]);
  const remainingEntries = entries.slice(1);
  const remainingCount = remainingEntries.length;

  const popoverContent = (
    <div className="overlay-popover-content-wrap">
      {remainingEntries.map(([key, value], index) => {
        const formatted = formatEntry(key, value as number);
        const colonIdx = formatted.indexOf(': ');
        const label = colonIdx >= 0 ? formatted.slice(0, colonIdx) : formatted;
        const val = colonIdx >= 0 ? formatted.slice(colonIdx + 2) : '';
        return (
          <div
            key={index}
            className="text-wrap"
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <span className="regular-2">{label}</span>
            <span className="regular-2">{val}</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      className={`v2-company-text-cell v2-company-array-cell v2-company-text-and-chip-cell ${className}`}
    >
      <span className="v2-company-cell-text-wrap v2-company-text-cell">
        <TruncatedTextWithAutoTooltip
          text={firstEntry}
          placement={Placement.Bottom}
        />
      </span>
      <CountPillWithPopover count={remainingCount} content={popoverContent} />
    </div>
  );
};

const EmployeeReviewCell: React.FC<{
  employeeReview?: {
    total_count?: number | null;
    aggregate_score?: number | null;
    business_outlook?: number | null;
    ceo_approval?: number | null;
    career_opportunities?: number | null;
    recommend?: number | null;
    work_life_balance?: number | null;
    culture_values?: number | null;
  } | null;
}> = ({ employeeReview }) => {
  if (!employeeReview) {
    return <span className="v2-company-text-cell">-</span>;
  }

  // Format label key (e.g., "business_outlook" -> "Business Outlook")
  const formatLabel = (key: string): string =>
    key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  // Get all entries except total_count
  const entries = Object.entries(employeeReview)
    .filter(
      ([key, value]) =>
        key !== 'total_count' &&
        value !== null &&
        value !== undefined &&
        (typeof value === 'number' || typeof value === 'string'),
    )
    .map(([key, value]) => [key, value] as [string, number | string]);

  // Check if total_count exists
  const hasTotalCount =
    employeeReview.total_count !== null &&
    employeeReview.total_count !== undefined;

  // If no total_count and no other entries, show dash
  if (!hasTotalCount && entries.length === 0) {
    return <span className="v2-company-text-cell">-</span>;
  }

  // Format first entry (always Total Count if it exists)
  let firstEntry: string;
  if (hasTotalCount) {
    firstEntry = `Total Count: ${employeeReview.total_count}`;
  } else if (entries.length > 0) {
    firstEntry = `${formatLabel(entries[0][0])}: ${entries[0][1]}`;
  } else {
    firstEntry = '-';
  }

  // If only total_count exists and no other entries, show it without chip (truncated)
  if (hasTotalCount && entries.length === 0) {
    return (
      <span className="v2-company-text-cell">
        <TruncatedTextWithAutoTooltip
          text={firstEntry}
          placement={Placement.Auto}
        />
      </span>
    );
  }

  // If no total_count but only one entry, show it without chip (truncated)
  if (!hasTotalCount && entries.length === 1) {
    return (
      <span className="v2-company-text-cell">
        <TruncatedTextWithAutoTooltip
          text={firstEntry}
          placement={Placement.Auto}
        />
      </span>
    );
  }

  // Multiple entries: show first (Total Count) truncated + "+N" chip for remaining entries
  const remainingCount = entries.length;
  const remainingEntries = entries;

  return (
    <div className="v2-company-text-cell v2-company-array-cell v2-company-text-and-chip-cell">
      <span className="v2-company-cell-text-wrap v2-company-text-cell">
        <TruncatedTextWithAutoTooltip
          text={firstEntry}
          placement={Placement.Auto}
        />
      </span>
      <CountPillWithPopover
        count={remainingCount}
        content={
          <div className="overlay-popover-content-wrap">
            {remainingEntries.map(([key, value], index) => (
              <div
                key={index}
                className="text-wrap d-flex justify-content-between"
              >
                <span className="regular-2">{formatLabel(key)}</span>
                <span className="regular-2">{value}</span>
              </div>
            ))}
          </div>
        }
      />
    </div>
  );
};

// Helper function to normalize executive data from company
const getExecutiveData = (
  newData?: ExecutiveData,
  legacyData?: ExecutiveData,
): ExecutiveData => (newData || legacyData) as ExecutiveData;

const ExecutiveChangeCell: React.FC<{
  executives?: ExecutiveData;
}> = ({ executives }) => {
  if (!executives || executives.length === 0) {
    return <span className="v2-company-text-cell">-</span>;
  }

  // Format each executive as "full_name : position_title"
  // Handle both new structure (member_full_name, member_position_title) and old structure (name, position)
  const formattedItems = executives
    .map((exec) => {
      const name = exec.member_full_name || exec.name || '';
      const position = exec.member_position_title || exec.position || '';
      if (!name && !position) return null;
      return `${name} : ${position}`;
    })
    .filter(Boolean) as string[];

  if (formattedItems.length === 0) {
    return <span className="v2-company-text-cell">-</span>;
  }

  // If only one item, just show it as text (truncated)
  if (formattedItems.length === 1) {
    return (
      <span className="v2-company-text-cell">
        <TruncatedTextWithAutoTooltip
          text={formattedItems[0]}
          placement={Placement.Bottom}
        />
      </span>
    );
  }

  // Multiple items: show first (truncated) + "+N" chip; popover shows only remaining (not displayed in column)
  const firstItem = formattedItems[0];
  const remainingItems = formattedItems.slice(1);
  const remainingCount = remainingItems.length;
  const remainingExecutives = executives.slice(1);

  const popoverContent = (
    <div className="overlay-popover-content-wrap">
      {remainingExecutives.map((exec, index) => (
        <div key={index} className="text-wrap d-flex justify-content-between">
          <span className="regular-2">
            {exec.member_full_name || exec.name || '-'}
          </span>
          <span className="regular-2">
            {exec.member_position_title || exec.position || '-'}
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="v2-company-text-cell v2-company-array-cell v2-company-text-and-chip-cell">
      <span className="v2-company-cell-text-wrap v2-company-text-cell">
        <TruncatedTextWithAutoTooltip
          text={firstItem}
          placement={Placement.Bottom}
        />
      </span>
      <CountPillWithPopover count={remainingCount} content={popoverContent} />
    </div>
  );
};

// Competitor Company Cell - popover same structure as Projected Salary Title (overlay-popover-content-wrap + text-wrap space-between)
const CompetitorCompanyCell: React.FC<{
  competitors?: Array<{
    company_name: string;
    similarity_score: number;
    competitors_website?: string;
  }> | null;
}> = ({ competitors }) => {
  const [showPopover, setShowPopover] = useState(false);
  const placement = useContext(RowPlacementContext);

  useClosePopoverOnScroll(showPopover, () => setShowPopover(false));

  if (!competitors || competitors.length === 0) {
    return <span className="v2-company-text-cell">-</span>;
  }

  const companyNames = competitors.map((c) => c.company_name).filter(Boolean);

  if (companyNames.length === 0) {
    return <span className="v2-company-text-cell">-</span>;
  }

  // Popover content: same as Projected Salary Title (label left, value right)
  const popoverContent = (
    <div className="overlay-popover-content-wrap">
      {competitors.map((competitor, index) => (
        <div key={index} className="text-wrap d-flex justify-content-between">
          <span className="regular-2">{competitor.company_name || '-'}</span>
          <span className="regular-2">
            {competitor.similarity_score !== null &&
            competitor.similarity_score !== undefined
              ? competitor.similarity_score
              : '-'}
          </span>
        </div>
      ))}
    </div>
  );

  // Format first competitor as "name: count" when count exists, else just "name"
  const formatFirstCompetitor = (competitor: typeof competitors[0]) => {
    const name = competitor.company_name || '-';
    // const count = competitor.similarity_score;
    return name;
  };

  const firstDisplayValue = formatFirstCompetitor(competitors[0]);

  // If only one competitor, show "name: count" (truncated) and make it clickable to show popover
  if (companyNames.length === 1) {
    return (
      <span className="v2-company-text-cell v2-company-array-cell">
        <OverlayPopover
          show={showPopover}
          onToggle={setShowPopover}
          placement={placement}
          className="prospect-tag-popover v2-company-popover"
          content={popoverContent}
          rootClose
        >
          <span
            className={`v2-company-text-cell ${
              showPopover ? 'active' : ''
            } clickable`}
            {...accessibleOnClick((e) => {
              e.stopPropagation();
              setShowPopover(!showPopover);
            })}
          >
            <TruncatedTextWithAutoTooltip
              text={firstDisplayValue}
              placement={Placement.Auto}
            />
          </span>
        </OverlayPopover>
      </span>
    );
  }

  // Multiple competitors: show first as "name: count" (truncated) + "+N" chip; popover shows only remaining (not displayed in column)
  const remainingCount = companyNames.length - 1;
  const remainingCompetitors = competitors.slice(1);

  const remainingPopoverContent = (
    <div className="overlay-popover-content-wrap">
      {remainingCompetitors.map((competitor, index) => (
        <div key={index} className="text-wrap d-flex justify-content-between">
          <span className="regular-2">{competitor.company_name || '-'}</span>
          {/* <span className="regular-2">
            {competitor.similarity_score !== null &&
              competitor.similarity_score !== undefined &&
              competitor.similarity_score}
          </span> */}
        </div>
      ))}
    </div>
  );

  return (
    <div className="v2-company-text-cell v2-company-array-cell v2-company-text-and-chip-cell">
      <span className="v2-company-cell-text-wrap v2-company-text-cell">
        <TruncatedTextWithAutoTooltip
          text={firstDisplayValue}
          placement={Placement.Auto}
        />
      </span>
      <CountPillWithPopover
        count={remainingCount}
        content={remainingPopoverContent}
        className="v2-company-competitor-count"
      />
    </div>
  );
};

// Helper to normalize string | string[] to string[]
const normalizeToStringArray = (
  value: string | string[] | null | undefined,
): string[] => {
  if (!value) return [];
  return Array.isArray(value) ? value.filter(Boolean) : [value].filter(Boolean);
};

// Company Phone Cell - shows first phone and "+N" for remaining (like people tab)
const CompanyPhoneCell: React.FC<{
  phoneNumbers?: string[] | null;
}> = ({ phoneNumbers }) => {
  const [showPopover, setShowPopover] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState<string | null>(null);
  const placement = useContext(RowPlacementContext);

  useClosePopoverOnScroll(showPopover, () => setShowPopover(false));

  if (!phoneNumbers || phoneNumbers.length === 0) {
    return <span className="v2-company-text-cell">-</span>;
  }

  const firstPhone = phoneNumbers[0];
  const remainingCount = phoneNumbers.length - 1;

  const handleCopy = async (text: string) => {
    try {
      await navigator?.clipboard?.writeText(text || '');
      setCopiedPhone(text);
      setTimeout(() => setCopiedPhone(null), 2000);
    } catch (error) {
      // Failed to copy phone - silently fail
    }
  };

  return (
    <div className="v2-company-phone-cell">
      <TruncatedTextWithAutoTooltip
        className="v2-company-phone-text"
        text={firstPhone || ''}
        telHref={toTelHref(firstPhone) ?? undefined}
        placement={Placement.Bottom}
      />
      <div
        className="v2-company-phone-copy-icon"
        {...accessibleOnClick(() => handleCopy(firstPhone))}
      >
        <OverlayTooltip
          placement={Placement.Bottom}
          text={copiedPhone === firstPhone ? 'Phone Copied' : 'Copy Phone'}
        >
          <span>{copiedPhone === firstPhone ? <Check /> : <Copy />}</span>
        </OverlayTooltip>
      </div>
      {remainingCount > 0 && (
        <OverlayPopover
          show={showPopover}
          onToggle={setShowPopover}
          placement={placement}
          className="v2-company-phones-popover-container"
          content={
            <div className="v2-company-phones-popover">
              {phoneNumbers?.slice(1).map((phone, index) => (
                <div key={index} className="v2-company-phone-item">
                  <TruncatedTextWithAutoTooltip
                    className="v2-company-phone-text"
                    text={phone || ''}
                    telHref={toTelHref(phone) ?? undefined}
                    placement={Placement.Bottom}
                  />
                  <div
                    className="v2-company-phone-copy-icon"
                    {...accessibleOnClick(() => handleCopy(phone))}
                  >
                    <OverlayTooltip
                      placement={Placement.Bottom}
                      text={
                        copiedPhone === phone ? 'Phone Copied' : 'Copy Phone'
                      }
                    >
                      <span>
                        {copiedPhone === phone ? <Check /> : <Copy />}
                      </span>
                    </OverlayTooltip>
                  </div>
                </div>
              ))}
            </div>
          }
          rootClose
        >
          <span
            className={`v2-company-phone-count ${showPopover ? 'active' : ''}`}
            // onClick={(e) => {
            //   e.stopPropagation();
            //   setShowPopover(!showPopover);
            // }}
            {...accessibleOnClick((e) => {
              e.stopPropagation();
              setShowPopover(!showPopover);
            })}
          >
            +{remainingCount}
          </span>
        </OverlayPopover>
      )}
    </div>
  );
};

// Company Email Cell - shows first email and "+N" for remaining (like people tab)
const CompanyEmailCell: React.FC<{
  emails?: string[] | null;
}> = ({ emails }) => {
  const [showPopover, setShowPopover] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);
  const placement = useContext(RowPlacementContext);

  useClosePopoverOnScroll(showPopover, () => setShowPopover(false));

  if (!emails || emails.length === 0) {
    return <span className="v2-company-text-cell">-</span>;
  }

  const firstEmail = emails[0];
  const remainingCount = emails.length - 1;

  const handleCopy = async (text: string) => {
    try {
      await navigator?.clipboard?.writeText(text || '');
      setCopiedEmail(text);
      setTimeout(() => setCopiedEmail(null), 2000);
    } catch (error) {
      // Failed to copy email - silently fail
    }
  };

  return (
    <div className="v2-company-email-cell">
      <TruncatedTextWithAutoTooltip
        className="v2-company-email-text"
        text={firstEmail || ''}
        placement={Placement.Bottom}
      />
      <div
        className="v2-company-email-copy-icon"
        {...accessibleOnClick(() => handleCopy(firstEmail))}
      >
        <OverlayTooltip
          placement={Placement.Bottom}
          text={copiedEmail === firstEmail ? 'Email Copied' : 'Copy Email'}
        >
          <span>{copiedEmail === firstEmail ? <Check /> : <Copy />}</span>
        </OverlayTooltip>
      </div>
      {remainingCount > 0 && (
        <OverlayPopover
          show={showPopover}
          onToggle={setShowPopover}
          placement={placement}
          className="v2-company-emails-popover-container"
          content={
            <div className="v2-company-emails-popover">
              {emails?.slice(1).map((email, index) => (
                <div key={index} className="v2-company-email-item">
                  <TruncatedTextWithAutoTooltip
                    className="v2-company-email-text"
                    text={email || ''}
                    placement={Placement.Auto}
                  />
                  <div
                    className="v2-company-email-copy-icon"
                    {...accessibleOnClick(() => handleCopy(email))}
                  >
                    <OverlayTooltip
                      placement={Placement.Bottom}
                      text={
                        copiedEmail === email ? 'Email Copied' : 'Copy Email'
                      }
                    >
                      <span>
                        {copiedEmail === email ? <Check /> : <Copy />}
                      </span>
                    </OverlayTooltip>
                  </div>
                </div>
              ))}
            </div>
          }
          rootClose
        >
          <span
            className={`v2-company-email-count ${showPopover ? 'active' : ''}`}
            // onClick={(e) => {
            //   e.stopPropagation();
            //   setShowPopover(!showPopover);
            // }}
            {...accessibleOnClick((e) => {
              e.stopPropagation();
              setShowPopover(!showPopover);
            })}
          >
            +{remainingCount}
          </span>
        </OverlayPopover>
      )}
    </div>
  );
};

// Number Cell component - truncates with tooltip
const NumberCell: React.FC<{
  value?: number | null;
  className?: string;
  formatToKM?: boolean;
}> = ({ value, className = '', formatToKM = false }) => {
  if (value === null || value === undefined) {
    return <span className={`v2-company-text-cell ${className}`}>-</span>;
  }
  const formatted = formatToKM
    ? formatNumberToKM(value)
    : formatNumberWithCommas(value);
  return (
    <span className={`v2-company-text-cell ${className}`}>
      <TruncatedTextWithAutoTooltip
        text={formatted}
        placement={Placement.Auto}
      />
    </span>
  );
};

// Stock Information Cell - same popover structure/SCSS as Employee count by department (overlay-popover-content-wrap + text-wrap)
const StockInformationCell: React.FC<{
  company: V2Company;
}> = ({ company }) => {
  const [showPopover, setShowPopover] = useState(false);
  const placement = useContext(RowPlacementContext);
  const currency = company.stock_currency;
  const date = company.stock_date;
  const marketcap = company.stock_marketcap;
  const closingPrice = company.stock_closing_price;

  useClosePopoverOnScroll(showPopover, () => setShowPopover(false));

  if (!currency && !date && !marketcap) {
    return <span className="v2-company-text-cell">-</span>;
  }

  const hasPopoverContent = date || marketcap;

  if (!hasPopoverContent) {
    return (
      <span className="v2-company-text-cell">{`$${closingPrice}` || '-'}</span>
    );
  }

  const popoverContent = (
    <div className="overlay-popover-content-wrap">
      <div className="text-wrap d-flex justify-content-between">
        <span className="regular-2">Currency</span>
        <span className="regular-2">{currency || '-'}</span>
      </div>
      {date && (
        <div className="text-wrap d-flex justify-content-between">
          <span className="regular-2">Date</span>
          <span className="regular-2">{date}</span>
        </div>
      )}
      {marketcap !== null && marketcap !== undefined && (
        <div
          className="text-wrap"
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <span className="regular-2">Market Cap</span>
          <span className="regular-2">{formatCurrencyValue(marketcap)}</span>
        </div>
      )}
    </div>
  );

  return (
    <span className="v2-company-text-cell v2-company-array-cell">
      <OverlayPopover
        show={showPopover}
        onToggle={setShowPopover}
        placement={placement}
        className="prospect-tag-popover v2-company-popover"
        content={popoverContent}
        rootClose
      >
        <OverlayTooltip
          text="View Stock Information"
          placement={Placement.Bottom}
        >
          <span
            className={`v2-company-text-cell ${
              showPopover ? 'active' : ''
            } clickable`}
            {...accessibleOnClick((e) => {
              e.stopPropagation();
              setShowPopover(!showPopover);
            })}
          >
            <TruncatedTextWithAutoTooltip
              text={closingPrice != null ? `$${closingPrice}` : '-'}
              placement={Placement.Auto}
            />
          </span>
        </OverlayTooltip>
      </OverlayPopover>
    </span>
  );
};

// Product Review Score Cell - same popover structure/SCSS as Stock (overlay-popover-content-wrap + text-wrap)
const ProductReviewScoreCell: React.FC<{
  company: V2Company;
}> = ({ company }) => {
  const [showPopover, setShowPopover] = useState(false);
  const aggregateScore = company.product_reviews_aggregate_score;
  const count = company.product_reviews_count;
  const scoreChangeCurrent = company.product_reviews_score_change_current;
  const scoreChangeQuarterly = company.product_reviews_score_change_quarterly;
  const scoreChangeYearly = company.product_reviews_score_change_yearly;
  const scoreChangeMonthly = company.product_reviews_score_change_monthly;

  const hasAny =
    (aggregateScore !== null && aggregateScore !== undefined) ||
    (count !== null && count !== undefined) ||
    (scoreChangeCurrent !== null && scoreChangeCurrent !== undefined) ||
    (scoreChangeQuarterly !== null && scoreChangeQuarterly !== undefined) ||
    (scoreChangeYearly !== null && scoreChangeYearly !== undefined) ||
    (scoreChangeMonthly !== null && scoreChangeMonthly !== undefined);

  if (!hasAny) {
    return <span className="v2-company-text-cell">-</span>;
  }
  // Primary display must show product reviews count.
  const primary = count !== null && count !== undefined ? String(count) : '-';

  const fullItems: Array<{ label: string; value: string }> = [];
  if (aggregateScore !== null && aggregateScore !== undefined) {
    fullItems.push({ label: 'Aggregate score', value: String(aggregateScore) });
  }
  if (scoreChangeCurrent !== null && scoreChangeCurrent !== undefined) {
    fullItems.push({
      label: 'Current score change',
      value: String(scoreChangeCurrent),
    });
  }
  if (scoreChangeMonthly !== null && scoreChangeMonthly !== undefined) {
    fullItems.push({
      label: 'Monthly Score Change',
      value: String(scoreChangeMonthly),
    });
  }
  if (scoreChangeQuarterly !== null && scoreChangeQuarterly !== undefined) {
    fullItems.push({
      label: 'Quarterly score change',
      value: String(scoreChangeQuarterly),
    });
  }
  if (scoreChangeYearly !== null && scoreChangeYearly !== undefined) {
    fullItems.push({
      label: 'Yearly score change',
      value: String(scoreChangeYearly),
    });
  }

  if (fullItems.length === 0) {
    return <span className="v2-company-text-cell">-</span>;
  }

  const popoverContent = (
    <div className="overlay-popover-content-wrap">
      {fullItems.map((item, idx) => (
        <div key={idx} className="text-wrap d-flex justify-content-between">
          <span className="regular-2">{item.label}</span>
          <span className="regular-2">{item.value}</span>
        </div>
      ))}
    </div>
  );

  // +N chip should show the number of items present in the dropdown (fullItems length)
  const dropdownCount = fullItems.length;

  return (
    <div className="v2-company-text-cell v2-company-array-cell">
      <span className="v2-company-cell-text-wrap v2-company-text-cell">
        <TruncatedTextWithAutoTooltip
          text={primary}
          placement={Placement.Auto}
        />
      </span>
      {dropdownCount > 0 && (
        <CountPillWithPopover count={dropdownCount} content={popoverContent} />
      )}
    </div>
  );
};

// Format date from YYYY-MM-DD to "Jan 2022" format
const formatFundingDate = (dateString?: string | null): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${month} ${year}`;
  } catch {
    return dateString;
  }
};

// Funding Round Cell - shows name, popup table with amount, date, and investors
const FundingRoundCell: React.FC<{
  company: V2Company;
}> = ({ company }) => {
  const [showPopover, setShowPopover] = useState(false);
  const placement = useContext(RowPlacementContext);
  const rounds = company.funding_rounds;

  useClosePopoverOnScroll(showPopover, () => setShowPopover(false));

  if (!rounds || rounds.length === 0) {
    return <span className="v2-company-text-cell">-</span>;
  }

  // Get the first round's name to display
  const firstRound = rounds[0];
  const firstName = firstRound?.name;

  if (!firstName) {
    return <span className="v2-company-text-cell">-</span>;
  }

  const columns: CompanyDataCardColumn[] = [
    {
      key: 'fundingRound',
      label: 'Funding Round',
      align: 'left',
    },
    {
      key: 'amount',
      label: 'Funding Amount',
      align: 'left',
    },
    {
      key: 'date',
      label: 'Date',
      align: 'left',
    },
    {
      key: 'investors',
      label: 'No. of Investors',
      align: 'left',
    },
  ];

  const rows: CompanyDataCardRow[] = rounds.map((round, index) => ({
    id: `${round?.name ?? 'round'}-${index}`,
    values: {
      fundingRound: round?.name || '-',
      amount:
        round?.amount_raised !== null && round?.amount_raised !== undefined
          ? formatCurrencyValue(round.amount_raised)
          : '-',
      date: formatFundingDate(round?.announced_date) || '-',
      investors:
        round?.num_investors !== null && round?.num_investors !== undefined
          ? round.num_investors
          : '-',
    },
  }));

  const remainingCount = rounds.length - 1;

  const popoverContent = (
    <div className="v2-company-funding-round-popover" style={{ width: 600 }}>
      <CompanyDataCard columns={columns} rows={rows} />
    </div>
  );

  return (
    <div className="v2-company-funding-round-cell">
      <OverlayPopover
        show={showPopover}
        onToggle={setShowPopover}
        placement={placement}
        className="v2-company-funding-round-popover-container"
        content={popoverContent}
        rootClose
      >
        <div
          className={`v2-company-text-cell v2-company-funding-round-text v2-company-text-and-chip-cell ${
            showPopover ? 'active' : ''
          } clickable`}
          {...accessibleOnClick((e) => {
            e.stopPropagation();
            setShowPopover(!showPopover);
          })}
        >
          <span className="v2-company-cell-text-wrap v2-company-text-cell">
            <TruncatedTextWithAutoTooltip
              text={firstName}
              placement={Placement.Auto}
            />
          </span>
          {rounds.length > 1 && (
            <span className="v2-company-funding-round-count-pill">
              +{remainingCount}
            </span>
          )}
        </div>
      </OverlayPopover>
    </div>
  );
};

// Percentage Cell component - truncates with tooltip
const PercentageCell: React.FC<{
  value?: number | null;
  className?: string;
}> = ({ value, className = '' }) => {
  if (value === null || value === undefined) {
    return <span className={`v2-company-text-cell ${className}`}>-</span>;
  }
  const displayText = `${value}%`;
  return (
    <span className={`v2-company-text-cell ${className}`}>
      <TruncatedTextWithAutoTooltip
        text={displayText}
        placement={Placement.Auto}
      />
    </span>
  );
};

// LinkedIn Cell
const LinkedInCell: React.FC<{ url?: string | null }> = ({ url }) => {
  if (!url) {
    return <span className="v2-company-text-cell">-</span>;
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="v2-company-linkedin-link"
      onClick={(e) => e.stopPropagation()}
    >
      <LinkedinSquareFilled width={16} height={16} />
    </a>
  );
};

// Location Cell
const LocationCell: React.FC<{
  company: V2Company;
}> = ({ company }) => {
  // Prefer hq_location string if available
  if (company.hq_full_address) {
    return <TextCell value={company.hq_full_address} />;
  }
  // Otherwise use separate fields
  const parts = [company.hq_city, company.hq_state, company.hq_country].filter(
    Boolean,
  );
  const location = parts.length > 0 ? parts.join(', ') : undefined;
  return <TextCell value={location} />;
};

// Row Actions Cell - three dots menu with Add to list, Add Tag, Remove Tag, Remove From List
type RowActionOptions = { skipSelection?: boolean };

const CompanyRowActionsCell: React.FC<{
  company: V2Company;
  onAddToList: (companyId: number) => void;
  onAddTag: (companyId: number, options?: RowActionOptions) => void;
  onRemoveTag: (companyId: number, options?: RowActionOptions) => void;
  onRemoveFromList: (companyId: number) => void;
  hasListPermission?: boolean;
  activeSubTab: LeadFinderSecondaryTab;
  isEnriching?: boolean;
}> = ({
  company,
  onAddToList,
  onAddTag,
  onRemoveTag,
  onRemoveFromList,
  hasListPermission = true,
  activeSubTab,
  isEnriching = false,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const companyId = company.id ?? company.company_id ?? 0;

  useEffect(() => {
    if (isEnriching) {
      setShowDropdown(false);
    }
  }, [isEnriching]);

  const handleAction = (
    e: React.MouseEvent | React.KeyboardEvent | undefined,
    callback: () => void,
  ) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setShowDropdown(false);
    callback();
  };

  return (
    <div
      className="v2-company-row-actions-cell"
      {...accessibleOnClick((e) => e.stopPropagation())}
    >
      <Dropdown
        bsPrefix="dropdown-custom"
        drop="down"
        show={showDropdown && !isEnriching}
        onToggle={(nextShow) => {
          if (isEnriching) return;
          setShowDropdown(Boolean(nextShow));
        }}
      >
        {isEnriching ? (
          <OverlayTooltip placement={Placement.Bottom} text="Enriching...">
            <span
              className="v2-company-row-actions-toggle v2-company-row-actions-toggle--disabled"
              aria-disabled="true"
            >
              <DotsVertical width={16} height={16} color="#6B7280" />
            </span>
          </OverlayTooltip>
        ) : (
          <Dropdown.Toggle
            bsPrefix="dropdown-toggle-custom"
            variant="dropdown-custom"
            className={`v2-company-row-actions-toggle ${
              showDropdown ? 'active' : ''
            }`}
          >
            <DotsVertical width={16} height={16} color="#6B7280" />
          </Dropdown.Toggle>
        )}

        {!isEnriching &&
          createPortal(
            <Dropdown.Menu
              bsPrefix="dropdown-menu-custom"
              className="v2-company-row-actions-dropdown"
              // Prevent click-through to underlying table (checkbox selection) since this menu is portaled to <body>
              onMouseDownCapture={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <div className="v2-company-row-actions-menu">
                {hasListPermission && (
                  <Dropdown.Item
                    as="div"
                    className="v2-company-row-actions-menu-item"
                    {...accessibleOnClick((e) =>
                      handleAction(e, () => onAddToList(companyId)),
                    )}
                  >
                    <AddList width={16} height={16} className="gray-txt-15" />
                    <span className="regular-2 blue-txt-16">Save to list</span>
                  </Dropdown.Item>
                )}

                <Dropdown.Item
                  as="div"
                  className="v2-company-row-actions-menu-item"
                  {...accessibleOnClick((e) =>
                    handleAction(e, () =>
                      onAddTag(companyId, { skipSelection: true }),
                    ),
                  )}
                >
                  <AddTag width={16} height={16} className="gray-txt-15" />
                  <span className="regular-2 blue-txt-16">Add Tag</span>
                </Dropdown.Item>

                <Dropdown.Item
                  as="div"
                  className="v2-company-row-actions-menu-item"
                  {...accessibleOnClick((e) =>
                    handleAction(e, () =>
                      onRemoveTag(companyId, { skipSelection: true }),
                    ),
                  )}
                >
                  <RemoveTag width={16} height={16} className="gray-txt-15" />
                  <span className="regular-2 blue-txt-16">Remove Tag</span>
                </Dropdown.Item>

                {hasListPermission && (
                  <Dropdown.Item
                    as="div"
                    className="v2-company-row-actions-menu-item"
                    {...accessibleOnClick((e) =>
                      handleAction(e, () => onRemoveFromList(companyId)),
                    )}
                  >
                    <RemoveList
                      width={16}
                      height={16}
                      className="gray-txt-15"
                    />
                    <span className="regular-2 blue-txt-16">
                      Remove From List
                    </span>
                  </Dropdown.Item>
                )}
              </div>
            </Dropdown.Menu>,
            document.body,
          )}
      </Dropdown>
    </div>
  );
};

// Actions Cell
const CompanyActionsCell: React.FC<{
  company: V2Company;
  onSaveCompany: (companyId: number) => void;
  onFindPeople: (company: V2Company) => void;
  isSaving?: boolean;
  activeSubTab: LeadFinderSecondaryTab;
  isRevealing?: boolean;
}> = ({
  company,
  onSaveCompany,
  onFindPeople,
  isSaving,
  activeSubTab,
  isRevealing = false,
}) => {
  const { isSaved } = company;

  return (
    <div className="v2-company-actions-cell">
      <Button
        variant="tertiary"
        className="v2-company-action-btn find-people-btn"
        onClick={(e) => {
          e.stopPropagation();
          onFindPeople(company);
        }}
      >
        <div className="d-flex gap-8 align-items-center">
          <Users width={16} height={16} />
          <span>View People</span>
        </div>
      </Button>

      <OverlayTooltip
        text={isRevealing ? 'Enriching...' : 'Save to List'}
        placement={Placement.Bottom}
      >
        <Button
          variant="tertiary"
          className="v2-company-action-btn add-to-list-btn"
          onClick={(e) => {
            e.stopPropagation();
            if (!isSaved && !isRevealing) {
              onSaveCompany(company.id || company.company_id || 0);
            }
          }}
          disabled={isSaved || isSaving || isRevealing}
          isLoading={isSaving}
        >
          <AddList width={16} height={16} />
        </Button>
      </OverlayTooltip>
    </div>
  );
};

export interface CompanyColumnDefinitionCallbacks {
  onSaveCompany: (companyId: number) => void;
  onFindPeople: (company: V2Company) => void;
  savingCompanyIds?: Record<number, boolean>;
  activeSubTab: LeadFinderSecondaryTab;
  onRowAddToList?: (companyId: number) => void;
  onRowAddTag?: (companyId: number, options?: RowActionOptions) => void;
  onRowRemoveTag?: (companyId: number, options?: RowActionOptions) => void;
  onRowRemoveFromList?: (companyId: number) => void;
  hasListPermission?: boolean;
  onRevealCompany?: (companyId: number) => void;
  enrichingCompanies?: Record<number, boolean>;
  isFreePlanRestricted?: boolean;
  onCompanyProfileClick?: (company: V2Company, rect: DOMRect) => void;
  companyFeedbackCache?: Record<
    number,
    { sentiment: 'positive' | 'negative'; message?: string }
  >;
  onCompanyFeedbackSubmitted?: (
    companyId: number,
    sentiment: LeadRevealFeedbackSentiment,
    message?: string,
  ) => void;
}

export const getCompanyColumnDefinitions = (
  callbacks: CompanyColumnDefinitionCallbacks,
) => {
  const {
    onSaveCompany,
    onFindPeople,
    savingCompanyIds,
    activeSubTab,
    onRowAddToList,
    onRowAddTag,
    onRowRemoveTag,
    onRowRemoveFromList,
    hasListPermission = true,
    onRevealCompany,
    enrichingCompanies = {},
    isFreePlanRestricted = false,
    onCompanyProfileClick,
    companyFeedbackCache,
    onCompanyFeedbackSubmitted,
  } = callbacks;

  // Company columns allowed for free plan users (not restricted)
  // Includes data columns + system/attribute columns that should never show teaser
  const COMPANY_ALLOWED_COLUMN_IDS = new Set([
    'company_name',
    'socials',
    'hq_location',
    'company_description',
    // System/attribute columns — should work as before
    'actions',
    'list',
    'tags',
    'company_phone',
    'company_email',
    'row_actions',
  ]);

  // Helper function to get enriching state for a company
  const getIsEnriching = (company: V2Company): boolean => {
    const companyId = Number(company.id || company.company_id || 0);
    return (
      Boolean(company.isRevealing) || Boolean(enrichingCompanies?.[companyId])
    );
  };

  const columns = [
    // Default columns (Yes in CSV)
    columnHelper.display({
      id: 'company_name',
      header: 'Company',
      size: 250,
      minSize: 250,
      cell: ({ row }) => {
        const company = row.original;
        const companyId = company.id ?? company.company_id ?? 0;
        const isEnriched = company.isEnriched === true;
        const cachedFeedback =
          companyFeedbackCache?.[companyId] ?? company.revealFeedback;
        return (
          <CompanyNameCell
            company={company}
            onProfileClick={onCompanyProfileClick}
            feedbackProps={
              isEnriched
                ? {
                    companyId,
                    initialSentiment: cachedFeedback?.sentiment,
                    initialMessage: cachedFeedback?.message,
                    onFeedbackSubmitted: (sentiment, message) =>
                      onCompanyFeedbackSubmitted?.(
                        companyId,
                        sentiment,
                        message,
                      ),
                  }
                : undefined
            }
          />
        );
      },
    }),

    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      size: 212,
      cell: ({ row }) => (
        <CompanyActionsCell
          company={row.original}
          onSaveCompany={onSaveCompany}
          onFindPeople={onFindPeople}
          isSaving={
            savingCompanyIds?.[row.original.id || row.original.company_id || 0]
          }
          activeSubTab={activeSubTab}
          isRevealing={getIsEnriching(row.original)}
        />
      ),
    }),

    columnHelper.display({
      id: 'socials',
      header: 'Socials',
      size: 152,
      cell: ({ row }) => (
        <CompanySocialsCellWithContext
          socialUrls={row.original.social_urls || row.original.socials}
        />
      ),
    }),

    columnHelper.display({
      id: 'hq_location',
      header: 'Location',
      size: 200,
      cell: ({ row }) => <LocationCell company={row.original} />,
    }),

    columnHelper.display({
      id: 'founded_year',
      header: 'Founded Year',
      size: 120,
      cell: ({ row }) => (
        <TextCell
          value={
            row.original.founded_year
              ? String(row.original.founded_year)
              : undefined
          }
        />
      ),
    }),

    columnHelper.accessor('industry', {
      id: 'industry',
      header: 'Industry',
      size: 160,
      cell: ({ row }) => <TextCell value={row.original.industry} />,
    }),

    columnHelper.accessor('type', {
      id: 'type',
      header: 'Type',
      size: 120,
      cell: ({ row }) => <TextCell value={row.original.type} />,
    }),

    columnHelper.accessor('is_b2b', {
      id: 'is_b2b',
      header: 'Is B2B',
      size: 100,
      cell: ({ row }) => {
        let value = '-';
        if (row.original.is_b2b === true) {
          value = 'Yes';
        } else if (row.original.is_b2b === false) {
          value = 'No';
        }
        return <TextCell value={value} />;
      },
    }),

    columnHelper.display({
      id: 'sic_codes',
      header: 'SIC Codes',
      size: 150,
      cell: ({ row }) => <ArrayCellWithChip items={row.original.sic_codes} />,
    }),

    columnHelper.display({
      id: 'naics_codes',
      header: 'NAIC Codes',
      size: 150,
      cell: ({ row }) => <ArrayCellWithChip items={row.original.naics_codes} />,
    }),

    columnHelper.display({
      id: 'employee_count',
      header: 'Employee Count',
      size: 130,
      cell: ({ row }) => (
        <TextCell
          value={
            row.original.company_size_range ||
            formatEmployeeCount(
              row.original.employees_count || row.original.employee_count,
            )
          }
          formatToKM
          isCurrency={false}
        />
      ),
    }),

    columnHelper.display({
      id: 'list',
      header: 'List',
      size: 160,
      cell: ({ row, table }) => {
        const totalRows = table.getRowModel?.()?.rows?.length ?? 0;
        const isPosition = getTagsRowIsPosition(row.index ?? 0, totalRows);
        return (
          <ProspectTagsRow
            tags={row.original.lists}
            isPosition={isPosition}
            closeOnScroll={true}
          />
        );
      },
    }),

    columnHelper.display({
      id: 'tags',
      header: 'Tags',
      size: 160,
      cell: ({ row, table }) => {
        const totalRows = table.getRowModel?.()?.rows?.length ?? 0;
        const isPosition = getTagsRowIsPosition(row.index ?? 0, totalRows);
        return (
          <ProspectTagsRow
            tags={row.original.tags}
            isPosition={isPosition}
            closeOnScroll={true}
          />
        );
      },
    }),

    // Non-default columns
    columnHelper.display({
      id: 'categories_and_keywords',
      header: 'Categories & Keywords',
      size: 200,
      cell: ({ row, table }) => {
        const company = row.original;
        const companyId = company.id || company.company_id;
        const isEnriching = getIsEnriching(company);
        if (
          shouldShowEnrichmentTeaser(
            activeSubTab,
            company.isEnriched,
            'categories_and_keywords',
            isFreePlanRestricted,
          )
        ) {
          return (
            <EnrichmentTeaserCell
              companyId={companyId}
              onRevealCompany={onRevealCompany || (() => {})}
              isEnriching={isEnriching}
            />
          );
        }
        const items = row.original.categories_and_keywords;
        if (!items || items.length === 0) {
          return <span className="v2-company-text-cell">-</span>;
        }
        const tags = items.map((item: string, index: number) => ({
          name: item,
          id: index,
        }));
        const totalRows = table.getRowModel?.()?.rows?.length ?? 0;
        const isPosition = getTagsRowIsPosition(row.index ?? 0, totalRows);
        return (
          <ProspectTagsRow
            tags={tags}
            maxTags={1}
            isPosition={isPosition}
            closeOnScroll={true}
          />
        );
      },
    }),

    columnHelper.display({
      id: 'technologies',
      header: 'Technologies',
      size: 200,
      cell: ({ row, table }) => {
        const company = row.original;
        const companyId = company.id || company.company_id;
        const isEnriching = getIsEnriching(company);
        if (
          shouldShowEnrichmentTeaser(
            activeSubTab,
            company.isEnriched,
            'technologies',
            isFreePlanRestricted,
          )
        ) {
          return (
            <EnrichmentTeaserCell
              companyId={companyId}
              onRevealCompany={onRevealCompany || (() => {})}
              isEnriching={isEnriching}
            />
          );
        }
        const items =
          row.original.technologies_used || row.original.technologies;
        if (!items || items.length === 0) {
          return <span className="v2-company-text-cell">-</span>;
        }
        const tags = items.map((item: string, index: number) => ({
          name: item,
          id: index,
        }));
        const totalRows = table.getRowModel?.()?.rows?.length ?? 0;
        const isPosition = getTagsRowIsPosition(row.index ?? 0, totalRows);
        return (
          <ProspectTagsRow
            tags={tags}
            maxTags={1}
            isPosition={isPosition}
            closeOnScroll={true}
          />
        );
      },
    }),

    columnHelper.accessor('last_funding_round_name', {
      id: 'last_funding_round',
      header: 'Last funding round',
      size: 150,
      cell: ({ row }) => {
        const company = row.original;
        const companyId = company.id || company.company_id;
        const isEnriching = getIsEnriching(company);
        if (
          shouldShowEnrichmentTeaser(
            activeSubTab,
            company.isEnriched,
            'last_funding_round',
            isFreePlanRestricted,
          )
        ) {
          return (
            <EnrichmentTeaserCell
              companyId={companyId}
              onRevealCompany={onRevealCompany || (() => {})}
              isEnriching={isEnriching}
            />
          );
        }
        return (
          <TextCell
            value={
              row.original.last_funding_round_name ||
              row.original.last_funding_round
            }
          />
        );
      },
    }),

    columnHelper.display({
      id: 'last_raised_amount',
      header: 'Last raised amount',
      size: 150,
      cell: ({ row }) => {
        const company = row.original;
        const companyId = company.id || company.company_id;
        const isEnriching = getIsEnriching(company);
        if (
          shouldShowEnrichmentTeaser(
            activeSubTab,
            company.isEnriched,
            'last_raised_amount',
            isFreePlanRestricted,
          )
        ) {
          return (
            <EnrichmentTeaserCell
              companyId={companyId}
              onRevealCompany={onRevealCompany || (() => {})}
              isEnriching={isEnriching}
            />
          );
        }
        return (
          <TextCell
            value={formatCurrencyValue(
              row.original.last_funding_round_amount_raised ||
                row.original.last_funding_amount,
            )}
            formatToKM
            isCurrency
          />
        );
      },
    }),

    columnHelper.display({
      id: 'last_funding_date',
      header: 'Last funding date',
      size: 150,
      cell: ({ row }) => {
        const company = row.original;
        const companyId = company.id || company.company_id;
        const isEnriching = getIsEnriching(company);
        if (
          shouldShowEnrichmentTeaser(
            activeSubTab,
            company.isEnriched,
            'last_funding_date',
            isFreePlanRestricted,
          )
        ) {
          return (
            <EnrichmentTeaserCell
              companyId={companyId}
              onRevealCompany={onRevealCompany || (() => {})}
              isEnriching={isEnriching}
            />
          );
        }
        return (
          <TextCell
            value={
              row.original.last_funding_round_announced_date ||
              row.original.last_funding_date
            }
          />
        );
      },
    }),

    columnHelper.display({
      id: 'last_funding_rounds_num_investors',
      header: 'Last Funding Round Num Investors',
      size: 220,
      cell: ({ row }) => {
        const numInvestors = row.original.last_funding_round_num_investors;
        if (numInvestors !== null && numInvestors !== undefined) {
          return <TextCell value={String(numInvestors)} />;
        }
        return <TextCell value={undefined} />;
      },
    }),

    columnHelper.display({
      id: 'lead_investors',
      header: 'Lead Investors',
      size: 200,
      cell: ({ row }) => {
        const lastRoundLeadInvestors = Array.isArray(
          row.original.last_funding_round_lead_investors,
        )
          ? row.original.last_funding_round_lead_investors.filter(Boolean)
          : [];

        if (lastRoundLeadInvestors.length > 0) {
          return <ArrayCellWithChip items={lastRoundLeadInvestors} />;
        }

        const fundingRounds = row.original.funding_rounds;
        if (fundingRounds && fundingRounds.length > 0) {
          const allInvestors = fundingRounds
            .flatMap((round) => round.lead_investors || [])
            .filter(Boolean);
          if (allInvestors.length === 0) {
            return <TextCell value={undefined} />;
          }
          return <ArrayCellWithChip items={allInvestors} />;
        }

        return <TextCell value={undefined} />;
      },
    }),

    // Funding Rounds sub-columns
    columnHelper.display({
      id: 'funding_rounds_name',
      header: 'Funding Round',
      size: 180,
      cell: ({ row }) => <FundingRoundCell company={row.original} />,
    }),

    columnHelper.display({
      id: 'funding_rounds_announced_date',
      header: 'Funding Round Date',
      size: 200,
      cell: ({ row }) => {
        const rounds = row.original.funding_rounds;
        if (rounds && rounds.length > 0) {
          const dates = rounds.map((r) => r.announced_date).filter(Boolean);
          if (dates.length === 0) {
            return <TextCell value={undefined} />;
          }
          return <ArrayCellWithChip items={dates} />;
        }
        return <TextCell value={undefined} />;
      },
    }),

    columnHelper.display({
      id: 'funding_rounds_lead_investors',
      header: 'Funding Round Lead Investors',
      size: 250,
      cell: ({ row }) => {
        const rounds = row.original.funding_rounds;
        if (rounds && rounds.length > 0) {
          const allInvestors = rounds
            .flatMap((r) => r.lead_investors || [])
            .filter(Boolean);
          if (allInvestors.length === 0) {
            return <TextCell value={undefined} />;
          }
          return <ArrayCellWithChip items={allInvestors} />;
        }
        return <TextCell value={undefined} />;
      },
    }),

    columnHelper.display({
      id: 'funding_rounds_amount_raised',
      header: 'Funding Round Amount',
      size: 200,
      cell: ({ row }) => {
        const rounds = row.original.funding_rounds;
        if (rounds && rounds.length > 0) {
          const amounts = rounds
            .map((r) => formatCurrencyToKM(r.amount_raised))
            .filter(Boolean);
          if (amounts.length === 0) {
            return <TextCell value={undefined} />;
          }
          return <ArrayCellWithChip items={amounts} />;
        }
        return <TextCell value={undefined} />;
      },
    }),

    columnHelper.display({
      id: 'funding_rounds_num_investors',
      header: 'Funding Round Investors',
      size: 220,
      cell: ({ row }) => {
        const rounds = row.original.funding_rounds;
        if (rounds && rounds.length > 0) {
          const counts = rounds
            .map((r) => String(r.num_investors || rounds.length))
            .filter(Boolean);
          if (counts.length === 0) {
            return <TextCell value={undefined} />;
          }
          return <ArrayCellWithChip items={counts} />;
        }
        return <TextCell value={undefined} />;
      },
    }),

    columnHelper.display({
      id: 'annual_revenue',
      header: 'Annual Revenue',
      size: 150,
      cell: ({ row }) => {
        const company = row.original;
        const companyId = company.id || company.company_id;
        const isEnriching = getIsEnriching(company);
        if (
          shouldShowEnrichmentTeaser(
            activeSubTab,
            company.isEnriched,
            'annual_revenue',
            isFreePlanRestricted,
          )
        ) {
          return (
            <EnrichmentTeaserCell
              companyId={companyId}
              onRevealCompany={onRevealCompany || (() => {})}
              isEnriching={isEnriching}
            />
          );
        }
        return (
          <TextCell
            value={formatCurrencyValue(row.original.annual_revenue)}
            formatToKM
            isCurrency
          />
        );
      },
    }),

    columnHelper.display({
      id: 'employee_count_by_department',
      header: 'Employee Count by Department',
      size: 250,
      cell: ({ row }) => (
        <RecordCellWithChip
          record={
            row.original.employees_count_by_department ||
            row.original.department_counts
          }
          formatter={(key, value) => `${formatLabelKey(key)}: ${value}`}
        />
      ),
    }),

    columnHelper.display({
      id: 'employee_count_by_designation',
      header: 'Employee Count by Designation',
      size: 250,
      cell: ({ row }) => (
        <RecordCellWithChip
          record={
            row.original.employees_count_by_seniority ||
            row.original.seniority_counts
          }
          formatter={(key, value) => `${formatLabelKey(key)}: ${value}`}
        />
      ),
    }),

    columnHelper.display({
      id: 'projected_salary_title',
      header: 'Projected Salary - Title',
      size: 200,
      cell: ({ row }) => {
        const baseSalary = row.original.base_salary;

        if (Array.isArray(baseSalary) && baseSalary.length > 0) {
          const baseSalaryRecord: Record<string, number | null> = {};
          baseSalary.forEach((item) => {
            if (
              item?.title &&
              item?.salary_median !== null &&
              item?.salary_median !== undefined
            ) {
              baseSalaryRecord[item.title] = item.salary_median;
            }
          });

          if (Object.keys(baseSalaryRecord).length > 0) {
            return (
              <RecordCellWithChip
                record={baseSalaryRecord}
                formatter={(key, value) =>
                  `${key}: ${formatCurrencyValue(value)}`
                }
              />
            );
          }
        }

        // Final fallback to string value
        return <TextCell value={row.original.base_salary_title} />;
      },
    }),

    columnHelper.display({
      id: 'projected_salary_median',
      header: 'Projected Salary - Median',
      size: 250,
      cell: ({ row }) => (
        <TextCell
          value={formatCurrencyValue(row.original.base_salary_salary_median)}
        />
      ),
    }),

    columnHelper.display({
      id: 'company_phone',
      header: 'Company Phone',
      size: 150,
      cell: ({ row }) => {
        const company = row.original;
        const companyId = company.id || company.company_id;
        const isEnriching = getIsEnriching(company);
        if (
          shouldShowEnrichmentTeaser(
            activeSubTab,
            company.isEnriched,
            'company_phone',
            isFreePlanRestricted,
          )
        ) {
          return (
            <EnrichmentTeaserCell
              companyId={companyId}
              onRevealCompany={onRevealCompany || (() => {})}
              isEnriching={isEnriching}
            />
          );
        }
        const phoneNumbers =
          row.original.company_phone_numbers || row.original.company_phone;
        const phoneArray = normalizeToStringArray(phoneNumbers);

        if (phoneArray?.length > 0) {
          return <CompanyPhoneCell phoneNumbers={phoneArray} />;
        }
        return <TextCell value={undefined} />;
      },
    }),

    columnHelper.display({
      id: 'company_email',
      header: 'Company Email',
      size: 200,
      cell: ({ row }) => {
        const company = row.original;
        const companyId = company.id || company.company_id;
        const isEnriching = getIsEnriching(company);
        if (
          shouldShowEnrichmentTeaser(
            activeSubTab,
            company.isEnriched,
            'company_email',
            isFreePlanRestricted,
          )
        ) {
          return (
            <EnrichmentTeaserCell
              companyId={companyId}
              onRevealCompany={onRevealCompany || (() => {})}
              isEnriching={isEnriching}
            />
          );
        }
        const emails =
          row.original.company_emails || row.original.company_email;
        const emailArray = normalizeToStringArray(emails);

        if (emailArray?.length > 0) {
          return <CompanyEmailCell emails={emailArray} />;
        }
        return <TextCell value={undefined} />;
      },
    }),

    columnHelper.display({
      id: 'ownership_status',
      header: 'Ownership Type',
      size: 150,
      cell: ({ row }) => (
        <TextCell
          value={row.original.ownership_status || row.original.ownership_type}
        />
      ),
    }),

    // Competitors columns
    columnHelper.display({
      id: 'competitors_company_name',
      header: 'Competitor Company',
      size: 200,
      cell: ({ row: { original } }) => (
        <CompetitorCompanyCell competitors={original.competitors} />
      ),
    }),

    columnHelper.display({
      id: 'competitors_similarity_score',
      header: 'Competitor Similarity',
      size: 200,
      cell: ({ row: { original } }) => {
        const { competitors } = original;
        if (competitors && competitors.length > 0) {
          const scores = competitors.map(
            (c) => `${(c.similarity_score * 100).toFixed(1)}%`,
          );
          return <ArrayCellWithChip items={scores} />;
        }
        return <TextCell value={undefined} />;
      },
    }),

    columnHelper.display({
      id: 'competitors_competitors',
      header: 'Competitor Name',
      size: 200,
      cell: ({ row: { original } }) => {
        const { competitors } = original;
        if (competitors && competitors.length > 0) {
          const companyNames = competitors
            .map((c) => c.company_name)
            .filter(Boolean);
          return <ArrayCellWithChip items={companyNames} />;
        }
        return <TextCell value={undefined} />;
      },
    }),

    columnHelper.display({
      id: 'competitors_website',
      header: 'Competitor Website',
      size: 200,
      cell: ({ row: { original } }) => {
        const { competitors } = original;
        if (competitors && competitors.length > 0) {
          const websites = competitors
            .map((c) => c.competitors_website)
            .filter(Boolean);
          return <ArrayCellWithChip items={websites} />;
        }
        return <TextCell value={undefined} />;
      },
    }),

    columnHelper.display({
      id: 'active_job_posting_count',
      header: 'Active Job Posting Count',
      size: 200,
      cell: ({ row }) => (
        <NumberCell value={row.original.active_job_postings_count} />
      ),
    }),

    columnHelper.display({
      id: 'active_job_posting_title_wise',
      header: 'Active Job Posting Title wise',
      size: 250,
      cell: ({ row }) => {
        const titles = row.original.active_job_postings_title;
        const postings = row.original.active_job_postings;
        if (titles && Array.isArray(titles) && titles.length > 0) {
          return <ArrayCellWithChip items={titles} />;
        }
        if (postings && postings.length > 0) {
          const postingTitles = postings
            .map((p) => p.job_posting_title)
            .filter(Boolean);
          return <ArrayCellWithChip items={postingTitles} />;
        }
        return <TextCell value={undefined} />;
      },
    }),

    columnHelper.display({
      id: 'top_topics',
      header: 'Top Topics',
      size: 200,
      cell: ({ row }) => <ArrayCellWithChip items={row.original.top_topics} />,
    }),

    columnHelper.display({
      id: 'product_services',
      header: 'Product & Services',
      size: 200,
      cell: ({ row }) => (
        <ArrayCellWithChip items={row.original.product_services} />
      ),
    }),

    columnHelper.display({
      id: 'website_visitor_count',
      header: 'Website Visitor Count',
      size: 180,
      cell: ({ row }) => (
        <NumberCell
          value={
            row.original.total_website_visits_monthly ??
            row.original.website_visitors_last_month
          }
          formatToKM
        />
      ),
    }),

    columnHelper.display({
      id: 'website_visitor_count_by_country',
      header: 'Website Visitor Count By Country',
      size: 250,
      cell: ({ row }) => {
        const breakdown = row.original.visits_breakdown_by_country;
        if (breakdown && breakdown.length > 0) {
          const items = breakdown.map(
            (item) => `${item.country}: ${item.percentage.toFixed(1)}%`,
          );
          return <ArrayCellWithChip items={items} popoverLabelValue />;
        }
        return <TextCell value={undefined} />;
      },
    }),

    columnHelper.display({
      id: 'website_visitor_count_by_gender',
      header: 'Website Visitor Count By Gender',
      size: 250,
      cell: ({ row }) => {
        const breakdown = row.original.visits_breakdown_by_gender;
        if (breakdown) {
          const parts: string[] = [];
          if (breakdown.male_percentage !== undefined) {
            parts.push(`Male: ${breakdown.male_percentage.toFixed(1)}%`);
          }
          if (breakdown.female_percentage !== undefined) {
            parts.push(`Female: ${breakdown.female_percentage.toFixed(1)}%`);
          }
          if (parts.length === 0) {
            return <TextCell value={undefined} />;
          }
          return <ArrayCellWithChip items={parts} popoverLabelValue />;
        }
        return <TextCell value={undefined} />;
      },
    }),

    columnHelper.display({
      id: 'linkedin_followers',
      header: 'LinkedIn Followers',
      size: 150,
      cell: ({ row }) => (
        <NumberCell
          value={
            row.original.followers_count_linkedin ??
            row.original.linkedin_followers
          }
          formatToKM
        />
      ),
    }),

    columnHelper.display({
      id: 'product_review_score',
      header: 'Product Review Score',
      size: 180,
      cell: ({ row }) => <ProductReviewScoreCell company={row.original} />,
    }),

    columnHelper.display({
      id: 'employee_review_score',
      header: 'Employee review score',
      size: 200,
      cell: ({ row }) => (
        <EmployeeReviewCell
          employeeReview={
            row.original.employee_review || row.original.employee_reviews
          }
        />
      ),
    }),

    columnHelper.display({
      id: 'company_description',
      header: 'Company Description',
      size: 300,
      cell: ({ row }) => (
        <TextCell
          value={
            row.original.description_enriched ||
            row.original.company_description
          }
          allowCopy
          tooltipPlacement={Placement.Right}
        />
      ),
    }),

    // columnHelper.display({
    //   id: 'headline_summary',
    //   header: 'Headline + Summary',
    //   size: 250,
    //   cell: ({ row }) => <TextCell value={row.original.headline_summary} />,
    // }),

    // Financial metrics
    columnHelper.display({
      id: 'ebit',
      header: 'Ebit',
      size: 120,
      cell: ({ row }) => (
        <TextCell value={formatCurrencyValue(row.original.ebit)} />
      ),
    }),

    columnHelper.display({
      id: 'ebitda',
      header: 'Ebitda',
      size: 120,
      cell: ({ row }) => (
        <TextCell value={formatCurrencyValue(row.original.ebitda)} />
      ),
    }),

    columnHelper.display({
      id: 'ebitda_margin',
      header: 'Ebitda Margin',
      size: 140,
      cell: ({ row }) => <PercentageCell value={row.original.ebitda_margin} />,
    }),

    columnHelper.display({
      id: 'ebit_margin',
      header: 'Ebit Margin',
      size: 130,
      cell: ({ row }) => <PercentageCell value={row.original.ebit_margin} />,
    }),

    columnHelper.display({
      id: 'gross_profit',
      header: 'Gross Profit',
      size: 140,
      cell: ({ row }) => (
        <TextCell value={formatCurrencyValue(row.original.gross_profit)} />
      ),
    }),

    columnHelper.display({
      id: 'gross_profit_margin',
      header: 'Gross Profit Margin',
      size: 170,
      cell: ({ row }) => (
        <PercentageCell value={row.original.gross_profit_margin} />
      ),
    }),

    columnHelper.display({
      id: 'net_income',
      header: 'Net Income',
      size: 130,
      cell: ({ row }) => (
        <TextCell
          value={formatCurrencyValue(row.original.net_income)}
          formatToKM
          isCurrency
        />
      ),
    }),

    columnHelper.display({
      id: 'total_operating_expense',
      header: 'Total Operating Expense',
      size: 200,
      cell: ({ row }) => (
        <TextCell
          value={formatCurrencyValue(row.original.total_operating_expense)}
        />
      ),
    }),

    columnHelper.display({
      id: 'cost_of_goods_sold',
      header: 'Cost Of Goods Sold',
      size: 180,
      cell: ({ row }) => (
        <TextCell
          value={formatCurrencyValue(row.original.cost_of_goods_sold)}
          formatToKM
          isCurrency
        />
      ),
    }),

    columnHelper.display({
      id: 'earning_per_share',
      header: 'Earning Per Share',
      size: 160,
      cell: ({ row }) => (
        <TextCell
          value={formatCurrencyValue(row.original.earnings_per_share)}
        />
      ),
    }),

    // Stock Information
    columnHelper.display({
      id: 'stock_currency',
      header: 'Stock Information',
      size: 180,
      cell: ({ row }) => <StockInformationCell company={row.original} />,
    }),

    // Key Executive Departure
    columnHelper.display({
      id: 'key_executive_departure_name',
      header: 'Executive Departure - Name',
      size: 250,
      cell: ({ row }) => (
        <ExecutiveChangeCell
          executives={getExecutiveData(
            row.original.key_executive_departures,
            row.original.key_executive_departure,
          )}
        />
      ),
    }),

    columnHelper.display({
      id: 'key_executive_departure_position',
      header: 'Executive Departure - Position',
      size: 250,
      cell: ({ row }) => {
        const departures = getExecutiveData(
          row.original.key_executive_departures,
          row.original.key_executive_departure,
        );
        if (departures && departures.length > 0) {
          const positions = departures
            .map((d) => d.member_position_title || d.position)
            .filter(Boolean);
          return <ArrayCellWithChip items={positions} />;
        }
        return <TextCell value={undefined} />;
      },
    }),

    columnHelper.display({
      id: 'key_executive_departure_date',
      header: 'Executive Departure - Date',
      size: 220,
      cell: ({ row }) => {
        const departures = getExecutiveData(
          row.original.key_executive_departures,
          row.original.key_executive_departure,
        );
        if (departures && departures.length > 0) {
          const dates = departures
            .map((d) => d.departure_date || d.date)
            .filter(Boolean);
          return <ArrayCellWithChip items={dates} />;
        }
        return <TextCell value={undefined} />;
      },
    }),

    // Key Executive Arrival
    columnHelper.display({
      id: 'key_executive_arrival_name',
      header: 'Executive Arrival - Name',
      size: 250,
      cell: ({ row }) => (
        <ExecutiveChangeCell
          executives={getExecutiveData(
            row.original.key_executive_arrivals,
            row.original.key_executive_arrival,
          )}
        />
      ),
    }),

    columnHelper.display({
      id: 'key_executive_arrival_position',
      header: 'Executive Arrival - Position',
      size: 250,
      cell: ({ row }) => {
        const arrivals = getExecutiveData(
          row.original.key_executive_arrivals,
          row.original.key_executive_arrival,
        );
        if (arrivals && arrivals.length > 0) {
          const positions = arrivals
            .map((a) => a.member_position_title || a.position)
            .filter(Boolean);
          return <ArrayCellWithChip items={positions} />;
        }
        return <TextCell value={undefined} />;
      },
    }),

    columnHelper.display({
      id: 'key_executive_arrival_date',
      header: 'Executive Arrival - Date',
      size: 220,
      cell: ({ row }) => {
        const arrivals = getExecutiveData(
          row.original.key_executive_arrivals,
          row.original.key_executive_arrival,
        );
        if (arrivals && arrivals.length > 0) {
          const dates = arrivals
            .map((a) => a.arrival_date || a.date)
            .filter(Boolean);
          return <ArrayCellWithChip items={dates} />;
        }
        return <TextCell value={undefined} />;
      },
    }),

    // Existing columns
    columnHelper.display({
      id: 'domain',
      header: 'Domain',
      size: 160,
      cell: ({ row }) => (
        <DomainCell
          domain={row.original.website_domain || row.original.domain}
          website={row.original.website}
        />
      ),
    }),

    columnHelper.accessor('linkedin_url', {
      id: 'linkedin_url',
      header: 'LinkedIn',
      size: 100,
      cell: ({ row }) => <LinkedInCell url={row.original.linkedin_url} />,
    }),

    // Row actions - always last, static column with three dots menu (sticky right like sequence list table)
    ...(activeSubTab === LeadFinderSecondaryTab.SAVED &&
    onRowAddToList &&
    onRowAddTag &&
    onRowRemoveTag &&
    onRowRemoveFromList
      ? [
          columnHelper.display({
            id: 'row_actions',
            header: '',
            size: 46,
            minSize: 46,
            meta: {
              headerClassName: 'client-sequence-action-header',
              cellClassName: 'company-action',
            } as Record<string, string>,
            cell: ({ row }) => (
              <CompanyRowActionsCell
                company={row.original}
                isEnriching={getIsEnriching(row.original)}
                onAddToList={onRowAddToList}
                onAddTag={onRowAddTag}
                onRemoveTag={onRowRemoveTag}
                onRemoveFromList={onRowRemoveFromList}
                hasListPermission={hasListPermission}
                activeSubTab={activeSubTab}
              />
            ),
          }),
        ]
      : []),
  ];

  return columns?.map((col: any) => {
    if (!col?.id) {
      return col;
    }

    const originalCell = col.cell;
    const columnId = String(col.id);

    return {
      ...col,
      cell: (ctx: any) => {
        const { row, table } = ctx ?? {};
        const { original, index } = row ?? {};
        const { getRowModel } = table ?? {};

        const company: V2Company | undefined = original;

        const totalRows = getRowModel?.()?.rows?.length ?? 0;
        const rowIndex = index ?? 0;

        const isLastFiveRows = totalRows > 15 && rowIndex >= totalRows - 5;
        const cellPlacement = isLastFiveRows ? Placement.Top : Placement.Bottom;

        if (!company) {
          return (
            <RowPlacementContext.Provider value={cellPlacement}>
              {originalCell(ctx)}
            </RowPlacementContext.Provider>
          );
        }

        // Free plan restriction (additional layer, separate from existing teaser logic)
        if (
          isFreePlanRestricted &&
          activeSubTab !== LeadFinderSecondaryTab.SAVED &&
          !company.isEnriched &&
          !COMPANY_ALLOWED_COLUMN_IDS.has(columnId)
        ) {
          const companyId = Number(company.id || company.company_id || 0);
          const isEnriching = getIsEnriching(company);
          return (
            <RowPlacementContext.Provider value={cellPlacement}>
              <EnrichmentTeaserCell
                companyId={companyId}
                onRevealCompany={(onRevealCompany || (() => {})) as any}
                isEnriching={isEnriching}
              />
            </RowPlacementContext.Provider>
          );
        }

        if (
          shouldShowEnrichmentTeaser(
            activeSubTab,
            company.isEnriched,
            columnId,
            isFreePlanRestricted,
          )
        ) {
          const companyId = Number(company.id || company.company_id || 0);
          const isEnriching = getIsEnriching(company);
          return (
            <RowPlacementContext.Provider value={cellPlacement}>
              <EnrichmentTeaserCell
                companyId={companyId}
                onRevealCompany={(onRevealCompany || (() => {})) as any}
                isEnriching={isEnriching}
              />
            </RowPlacementContext.Provider>
          );
        }

        return (
          <RowPlacementContext.Provider value={cellPlacement}>
            {originalCell(ctx)}
          </RowPlacementContext.Provider>
        );
      },
    };
  });
};
